import { useRouter } from '@application/utils';
import type { DogBreedWhereInput } from '@gql/graphql';
import { AnimalSizeEnum, DogType } from '@gql/graphql';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';

export const nonAllergicBreedsNames = [
  'poodle',
  'poodleminiature',
  'poodlemediumsize',
  'poodlestandard',

  'xoloitzquintle',
  'xoloitzquintleminiature',
  'xoloitzquintleintermediate',
  'xoloitzquintlestandard',

  'chinesecresteddog',
  'chinesecresteddoghairless',
  'chinesecresteddogpowderpuff',

  'havanese',
  'maltese',
  'bichonfris',

  'schnauzer',
  'schnauzerstandard',
  'miniatureschnauzer',
  'giantschnauzer',

  'shihtzu',
  'cotondetulear',
  'bouvierdesflandres',
  'germanshorthairedpointer',
  'afghanhound',
  'basenji',
  'americanhairlessterrier',
  'yorkshireterrier',
  'samoyed',

  'airedaleterrier',
  'borderterrier',
  'irishterrier',
  'labradoodle',
  'australiansilkyterrier',
  'bedlingtonterrier',
  'irishwaterspaniel',
  'kerryblueterrier',
  'lhasaapso',
  'portuguesewaterdog',
  'spanishwaterdog',
  'puli',
  'brusselsgriffon',
];

const FILTERS: Record<string, Record<string, DogBreedWhereInput>> = {
  0: {
    pet: {
      dogType: { has: DogType.PET },
    },
    watch: {
      dogType: { has: DogType.WATCH },
    },
    sport: {
      dogType: { has: DogType.SPORT },
    },
  },
  1: {
    apartement: {
      AND: [
        { barkingTendancyIndex: { in: [1, 2, 3] } },
        { sizeCategory: { in: [AnimalSizeEnum.SMALL, AnimalSizeEnum.MEDIUM] } },
      ],
    },
  },
  2: {
    'no-garden': {
      AND: [{ exerciseIndex: { in: [1, 2] } }, { physicalActivityNeedIndex: { in: [1, 2, 3] } }],
    },
    'small-garden': {
      AND: [{ exerciseIndex: { in: [1, 2] } }, { physicalActivityNeedIndex: { in: [1, 2, 3, 4] } }],
    },
    'medium-garden': {
      AND: [
        { exerciseIndex: { in: [1, 2, 3] } },
        { physicalActivityNeedIndex: { in: [1, 2, 3, 4] } },
      ],
    },
  },
  3: {
    'less-8': {
      AND: [
        { sociabilityIndex: { in: [2, 3] } },
        { educationIndex: { in: [1, 2] } },
        { foreignerSociabilityIndex: { in: [2, 3, 4, 5] } },
      ],
    },
    'more-8': {
      AND: [
        { independenceIndex: { in: [1, 2, 3, 4] } },
        { educationIndex: { in: [1, 2] } },
        { sociabilityIndex: { in: [2, 3] } },
      ],
    },
  },
  4: { yes: { name: { in: nonAllergicBreedsNames } } },
  5: {
    no: {
      AND: [
        { educationIndex: { in: [1, 2] } },
        { predatoryInstinctIndex: { in: [1, 2, 3, 4] } },
        { foreignerSociabilityIndex: { in: [2, 3, 4, 5] } },
        { sociabilityIndex: { in: [2, 3] } },
      ],
    },
    yesPreviously: {
      AND: [
        { educationIndex: { in: [1, 2] } },
        { predatoryInstinctIndex: { in: [1, 2, 3, 4] } },
        { foreignerSociabilityIndex: { in: [2, 3, 4, 5] } },
        { sociabilityIndex: { in: [2, 3] } },
      ],
    },
  },
  6: {
    once: {
      AND: [{ physicalActivityNeedIndex: { in: [1, 2, 3] } }, { exerciseIndex: { in: [1, 2] } }],
    },
    'two-three': {
      AND: [{ physicalActivityNeedIndex: { in: [1, 2, 3, 4] } }, { exerciseIndex: { in: [1, 2] } }],
    },
  },
};

export const useBreedsWhereBuilder = () => {
  const { language } = useRouter();
  const breedsWhereBuilder = useCallback(
    (answers: any) => {
      let whereInputs: DogBreedWhereInput[] = [];

      const answerKeys = Object.keys(answers);

      // eslint-disable-next-line no-restricted-syntax
      for (const answerKey of answerKeys) {
        const answer = answers[answerKey as keyof typeof answerKeys];
        const filter: DogBreedWhereInput | undefined = FILTERS?.[answerKey]?.[answer];

        let shouldNotAdd = false;

        if (answerKey === '2' && answers[6] === 'everyday') {
          shouldNotAdd = true;
        }

        if (answerKey === '6' && (answer === 'every' || answers[2] === 'big-garden')) {
          shouldNotAdd = true;
        }

        if (!shouldNotAdd && filter) {
          whereInputs.push(filter);
        }
      }

      whereInputs.push({
        translations: {
          some: {
            AND: [
              { description: { not: { equals: '' } }, language: { equals: language } },
              { description: { not: { equals: null } }, language: { equals: language } },
            ],
          },
        },
      });

      whereInputs = cleanDeep(whereInputs).filter((x) => x) as DogBreedWhereInput[];

      return { ...(whereInputs.length ? { AND: whereInputs } : {}) } as DogBreedWhereInput;
    },
    [language],
  );

  return { breedsWhereBuilder };
};
